import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import { Image } from "../../AbstractElements";
import { dynamicImage } from "../../Utils";
import { API } from "../../api/API";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import CommonTableNew from "../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import FeatherIconCom from "../../Utils/CommonComponents/CommonIcons/FeatherIconCom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MonthDropdown from "../CommonCompo/MonthDropdown";

interface TerritoryItem {
  TerritoryId: number | string;
  TerritoryName: string;
}

interface TargetAchievementApiItem {
  TerritoryName?: string;
  TargetScope?: string;
  BrandName?: string;
  ProductName?: string;
  PobTarget?: number | string;
  PobAchived?: number | string;
}

interface TableRow {
  territory: string;
  targetScope: string;
  brand: string;
  product: string;
  target: string;
  achieved: string;
}

const TargetVsAchievement = () => {
  const [loading, setLoading] = useState(false);
  const serverUrl = localStorage.getItem("serverUrl");
  const [searchText, setSearchText] = useState("");
  const [tableData, setTableData] = useState<TargetAchievementApiItem[]>([]);
  const [territoryList, setTerritoryList] = useState<TerritoryItem[]>([]);
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");

  useEffect(() => {
    const targetAchievementData = async () => {
      try {
        if (!serverUrl) return;
        setLoading(true);

        const url = `${serverUrl}${API.targetAchievement}`;
        const params: Record<string, string> = {};
        if (selectedTerritoryId) params.territory_id = selectedTerritoryId;
        if (selectedMonth) params.moye = selectedMonth;

        const { status, data: responseData } = await getData(url, true, params);
        if (status === 200 && responseData?.data) {
          setTableData(responseData.data);
        } else {
          setTableData([]);
        }
      } catch (err) {
        console.error("Error fetching Target vs Achievement data:", err);
        setTableData([]);
      } finally {
        setLoading(false);
      }
    };

    targetAchievementData();
  }, [selectedTerritoryId, selectedMonth, serverUrl]);

  useEffect(() => {
    const getactiveVacantTerritories = async () => {
      try {
        if (!serverUrl) return;
        const url = `${serverUrl}${API.activeVacantTerritories}`;
        const { status, data: responseData } = await getData(url, true);
        if (status === 200 && responseData?.data) {
          setTerritoryList(responseData.data.activeTerritories);
        } else {
          setTerritoryList([]);
        }
      } catch (err) {
        console.error("Error fetching active vacant territories data:", err);
        setTerritoryList([]);
      }
    };
    getactiveVacantTerritories();
  }, [serverUrl]);

  const columns = [
    {
      id: "1",
      head: "Territory",
      key: "territory",
      sortKey: "territory",
    },
    {
      id: "2",
      head: "Target Scope",
      key: "targetScope",
      sortKey: "targetScope",
    },
    {
      id: "3",
      head: "Brand",
      key: "brand",
      sortKey: "brand",
    },
    {
      id: "4",
      head: "Product",
      key: "product",
      sortKey: "product",
    },
    {
      id: "5",
      head: "Target",
      key: "target",
      sortKey: "target",
    },
    {
      id: "6",
      head: "Achieved",
      key: "achieved",
      sortKey: "achieved",
    },
  ];

  const displayedData = useMemo<TableRow[]>(() => {
    if (!tableData?.length) return [];

    return tableData
      .map((item) => ({
        territory: item.TerritoryName ?? "-",
        targetScope: item.TargetScope ?? "-",
        brand: item.BrandName ?? "-",
        product: item.ProductName ?? "-",
        target:
          typeof item.PobTarget === "number"
            ? item.PobTarget.toLocaleString("en-IN")
            : (item.PobTarget ?? "0").toString(),
        achieved:
          typeof item.PobAchived === "number"
            ? item.PobAchived.toLocaleString("en-IN")
            : (item.PobAchived ?? "0").toString(),
      }))
      .filter((row) => {
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return (
          row.territory.toLowerCase().includes(search) ||
          row.targetScope.toLowerCase().includes(search) ||
          row.brand.toLowerCase().includes(search) ||
          row.product.toLowerCase().includes(search)
        );
      });
  }, [tableData, searchText]);

  const handleExportToExcel = () => {
    const exportData = displayedData.map((row) => ({
      Territory: row.territory,
      "Target Scope": row.targetScope,
      Brand: row.brand,
      Product: row.product,
      Target: row.target,
      Achieved: row.achieved,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Target Vs Achievement");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(blob, "Target_Vs_Achievement.xlsx");
  };

  return (
    <div className="page-body p-2">
      <Card>
        <div className="d-flex justify-content-between align-items-center pt-2 pb-2">
          <CardHeaderSpan
            headingClassName="pb-0"
            back={true}
            heading="Target Vs Achievement"
            span="ReportListTitle"
          />
          <div className="p-3">
            <MonthDropdown onChange={(value) => setSelectedMonth(value)} />
          </div>
        </div>

        <Container fluid>
          <div className="row mb-3 align-items-center justify-content-between">
            <div className="col-md-8 d-flex align-items-center gap-3 flex-wrap">
              <Input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by territory"
                className="form-control"
                style={{ maxWidth: "250px" }}
              />
              <Input
                type="select"
                value={selectedTerritoryId || ""}
                onChange={(e) => setSelectedTerritoryId(e.target.value)}
                style={{ maxWidth: "220px" }}
              >
                <option value="">Select Territory</option>
                {Array.isArray(territoryList) &&
                  territoryList.map((item: TerritoryItem) => (
                    <option
                      key={`${item.TerritoryId}-${item.TerritoryName}`}
                      value={String(item.TerritoryId)}
                    >
                      {item.TerritoryName}
                    </option>
                  ))}
              </Input>
            </div>

            <div className="col-md-4 d-flex justify-content-end gap-3">
              <button
                className="btn btn-sm btn-primary align-items-center d-flex"
                onClick={handleExportToExcel}
                disabled={displayedData.length === 0}
              >
                <FeatherIconCom
                  iconName="Download"
                  size={14}
                  className="text-light"
                />
                <span className="px-2" style={{ fontSize: 14 }}>
                  Export
                </span>
              </button>
            </div>
          </div>

          <Row>
            <Col sm={12}>
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-3">
                    <div>
                      <Image
                        className="b-r-10 img-80"
                        src={dynamicImage("dashboard/loader.gif")}
                        alt="Loading..."
                      />
                    </div>
                  </div>
                ) : (
                  <CommonTableNew
                    caption=""
                    headData={columns}
                    data={displayedData}
                    renderRow={(row, rowIndex) => (
                      <tr key={rowIndex}>
                        {columns.map((c) => (
                          <td key={c.key}>{(row as any)[c.key] ?? "-"}</td>
                        ))}
                      </tr>
                    )}
                  />
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </Card>
    </div>
  );
};

export default TargetVsAchievement;
